import type { MenuContext, MenuScreen } from '../types.js';
import type { GameRecord } from '../../history/types.js';
import { createScreenShell, createMenuHeader, createCrestButton, el, confirmDialog } from '@bn-games/ui-shell';
import { mountAccountHistorySection } from '@bn-games/history-core/ui';
import type { GameStats } from '@bn-games/game-history-client';
import { t } from '../../i18n/I18n.js';
import { openVerifierModal } from '../../verify/VerifierModal.js';
import { isLoggedIn } from '@bn-games/auth-client';
import { fetchDominoStats, fetchOnlineHistory } from '../../history/serverSync.js';
import { getRuleSet } from '../../game/variants/index.js';

interface RowOptions {
  readonly deletable: boolean;
}

function modeLabel(record: GameRecord): string {
  return t(`historyList.modes.${record.mode}`);
}

function tableLabel(record: GameRecord): string {
  switch (record.playerCount) {
    case 2:
      return t('playerCount.short.two');
    case 3:
      return t('playerCount.short.three');
    case 4:
      return getRuleSet(record.seed.variant).partnership
        ? t('playerCount.short.fourPartners')
        : t('playerCount.short.four');
  }
}

function variantLabel(record: GameRecord): string {
  return t(`settings.variants.${record.variant}`);
}

function resultLabel(record: GameRecord): string {
  if (record.result === 'draw') return t('historyList.draw');
  if (record.result === 'abandoned' || record.winnerSeats.length === 0) {
    return t('historyList.unfinished');
  }
  const names = record.winnerSeats.map((s) => t('result.seatNumber', { number: s + 1 })).join(', ');
  return t('historyList.winnerTemplate', { names });
}

function dateLabel(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Plays = `play` events only (drops draws/passes/muggins/next-hand from the count
// so the badge reflects how many tiles hit the table, not raw recorded events).
function playCount(record: GameRecord): number {
  let n = 0;
  for (const m of record.moves) {
    if (m.kind === 'play') n += 1;
  }
  return n;
}

export function createHistoryListScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  function paint(root: HTMLElement, ctx: MenuContext): void {
    while (root.firstChild) root.removeChild(root.firstChild);

    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;

    body.appendChild(createMenuHeader({ title: t('historyList.title') }));

    const signedIn = isLoggedIn();

    // "Your account" — signed in only; the server-synced online matches for
    // this game plus a stats summary. No-ops entirely when signed out.
    mountAccountHistorySection<GameRecord, GameStats>({
      body,
      strings: {
        title: t('historyList.accountSection.title'),
        loading: t('historyList.accountSection.loading'),
        empty: t('historyList.accountSection.empty'),
      },
      isLoggedIn,
      fetchRecords: fetchOnlineHistory,
      isStillMounted: () => frame !== null,
      renderRow: (rec) => createRow(rec, ctx, () => paint(root, ctx), { deletable: false }),
      fetchStats: fetchDominoStats,
      renderStats: (stats) => t('historyList.accountSection.statsLine', {
        wins: stats.wins,
        losses: stats.losses,
        draws: stats.draws,
        played: stats.gamesPlayed,
      }),
    });

    // "On this device" — the local HistoryStore list: offline modes always,
    // plus any online records from before signing in. Signed out, this is
    // the only section and stays unlabelled — byte-for-byte today's screen.
    if (signedIn) {
      body.appendChild(el('div', { class: 'history-row__title', text: t('historyList.deviceSection.title') }));
    }

    const records = ctx.historyStore.list();
    const list = el('div', { class: 'history-list' });

    if (records.length === 0) {
      list.appendChild(el('div', { class: 'history-empty', text: t('historyList.empty') }));
    } else {
      for (const rec of records) {
        list.appendChild(createRow(rec, ctx, () => paint(root, ctx), { deletable: true }));
      }
    }

    body.appendChild(list);

    const back = el('div', { class: 'button-row back-button-row' }, [
      createCrestButton({ label: t('common.back'), small: true, onClick: () => ctx.navigate('menu') }),
    ]);
    body.appendChild(back);

    root.appendChild(frame);
  }

  function createRow(record: GameRecord, ctx: MenuContext, repaint: () => void, options: RowOptions): HTMLElement {
    const movesText = t('historyList.movesCount', { count: playCount(record) });
    const subtitle = `${tableLabel(record)} · ${variantLabel(record)} · ${resultLabel(record)} · ${movesText}`;
    const meta = el('div', { class: 'history-row__meta' }, [
      el('div', { class: 'history-row__title', text: `${dateLabel(record.finishedAt)} — ${modeLabel(record)}` }),
      el('div', { class: 'history-row__sub', text: subtitle }),
    ]);

    const download = el('button', {
      class: 'history-row__icon-btn history-row__download',
      attrs: { type: 'button', 'aria-label': t('historyList.downloadAria'), title: t('historyList.downloadAria') },
      text: '⤓',
      on: {
        click: (e: Event) => {
          e.stopPropagation();
          downloadRecord(record);
        },
      },
    });

    const verify = record.seed.drandChainHash !== undefined
      ? el('button', {
          class: 'history-row__icon-btn history-row__verify',
          attrs: { type: 'button', 'aria-label': t('historyList.verifyAria'), title: t('historyList.verifyAria') },
          text: '🛡',
          on: {
            click: (e: Event) => {
              e.stopPropagation();
              openVerifierModal(record);
            },
          },
        })
      : null;

    const trash = options.deletable
      ? el('button', {
          class: 'history-row__icon-btn history-row__delete',
          attrs: { type: 'button', 'aria-label': t('historyList.deleteAria'), title: t('historyList.deleteAria') },
          text: '✕',
          on: {
            click: (e: Event) => {
              e.stopPropagation();
              void confirmDialog({ title: t('historyList.deleteConfirm'), message: '', danger: true }).then((ok) => {
                if (!ok) return;
                ctx.historyStore.delete(record.id);
                repaint();
              });
            },
          },
        })
      : null;

    const actions = el('div', { class: 'history-row__actions' }, [verify, download, trash]);

    return el('div', { class: 'history-row', on: { click: () => ctx.openReplay(record, 'history') } }, [meta, actions]);
  }

  function downloadRecord(record: GameRecord): void {
    const json = JSON.stringify(record, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const ts = new Date(record.finishedAt).toISOString().replace(/[:.]/g, '-');
    const shortId = record.id.slice(0, 8);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bn-domino-${shortId}-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    queueMicrotask(() => URL.revokeObjectURL(url));
  }

  return {
    mount(root, ctx) {
      paint(root, ctx);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
